/**
 * _priorityModel.js — the Priority Engine's SCORE, as pure functions (docs/PRIORITY_ENGINE.md, Phase 2).
 *
 * HELPER, NOT A ROUTE. Imported by BOTH `api/priority.js` (which computes the shadow ranking
 * server-side) and `frontend/src/services/priority.js` (which re-exports the scales and the
 * resolution helpers for the curation editor). One implementation, two consumers — deliberately not
 * the `lib/issuanceLayout.js` + `api/_issuanceLayout.js` mirror pattern, because a scoring formula
 * that silently drifts between the surface that shows it and the surface that computes it is a much
 * worse failure than a layout constant doing the same.
 *
 * NOTHING RANKS BY THIS YET. Phase 2 is shadow mode: computed, shown side-by-side against today's
 * ranking, consumed by nothing. Phase 4 is the first real consumer. Do not wire this into
 * api/flights.js's ordering or scripts/nightly_schedule.py.
 *
 * ── THE FORMULA, AND WHY ─────────────────────────────────────────────────────────────────────
 *
 *   score = (costOfDelay × projectDial ÷ effort) × unblocking × slippage × timePressure
 *                                                × outsideWaiting × phaseFuel
 *
 * The backbone is **CD3** (Cost of Delay ÷ Duration — docs/METHODOLOGY_REGISTER.md §3), on the
 * relative Fibonacci scale rather than dollars. Everything after the ÷ is a BOUNDED MULTIPLIER on
 * that backbone, derived from data the system already has, needing no human input.
 *
 * The split is not cosmetic. It encodes a property worth stating explicitly, because it is the
 * whole answer to Phase 0's central finding:
 *
 *   - The CD3 base spans roughly **1000×** (0.5 × 1 ÷ 13 ≈ 0.04 up to 3 × 13 ÷ 1 = 39).
 *   - The derived multipliers together span at most about **4×** (0.85 up to ~3.5).
 *
 * So the derived signals can reorder work *within* a band of comparable consequence, and cannot
 * leapfrog the whole scale. **Cost of delay over effort carries the ranking; nothing else does.**
 * That is the design constraint Phase 0 handed down (§2b item 3): today's Priority Score correlates
 * with the due date at r = 0.998, over a date that has been pushed up to 69 times on a single
 * milestone. A ranking that leans on that date reproduces the disease.
 *
 * ── ON THE WEIGHTS ───────────────────────────────────────────────────────────────────────────
 * Every weight lives in PRIORITY_WEIGHTS below, in one block, each with the reasoning for its
 * magnitude. They are meant to be tuned. Change them here and every surface changes together;
 * there is no second copy anywhere. The shapes (linear-to-cap, √, piecewise ramp) are chosen to be
 * legible on a page rather than optimal in a model — nobody can tune a curve they cannot picture.
 */

/* ─────────────────────────────────────────────────────────────────────────────
   The scales. Anchors live with the numbers so a score set in September still
   means the same thing in March, to someone who was not in the room.
   ───────────────────────────────────────────────────────────────────────────── */

export const FIBONACCI = [1, 2, 3, 5, 8, 13]

export const COST_OF_DELAY_SCALE = [
  { value: 1,  label: 'Nobody notices',      hint: 'Could slip a month and no one outside the studio would know.' },
  { value: 2,  label: 'Mild internal drag',  hint: 'Makes our own life harder, invisible to anyone else.' },
  { value: 3,  label: 'A client notices',    hint: 'Mild irritation, some trust cost, no hard consequence.' },
  { value: 5,  label: 'Someone is blocked',  hint: 'A consultant, contractor, or another milestone cannot proceed.' },
  { value: 8,  label: 'Money or a hard date',hint: 'A fee milestone, an agency deadline, a real cost to slipping.' },
  { value: 13, label: 'Something breaks',    hint: 'Permit window closes, a crew sits idle, a relationship is at risk.' },
]

export const EFFORT_SCALE = [
  { value: 1,  label: 'A morning',   hint: 'One sitting, one person.' },
  { value: 2,  label: 'A day',       hint: '' },
  { value: 3,  label: 'A few days',  hint: '' },
  { value: 5,  label: 'About a week',hint: '' },
  { value: 8,  label: 'A couple of weeks', hint: '' },
  { value: 13, label: 'Weeks of work',     hint: 'Big enough that it probably wants breaking up.' },
]

export const PROJECT_DIALS = [
  { value: 0.5, label: 'Background', hint: 'Real, but nothing here should jump the queue.' },
  { value: 1,   label: 'Normal',     hint: 'The default. No row needed to mean this.' },
  { value: 2,   label: 'Hot',        hint: 'Client pressure, a live deadline, or recovering a slip.' },
  { value: 3,   label: 'Critical',   hint: 'The firm should feel this one. Use sparingly or it means nothing.' },
]

/* ─────────────────────────────────────────────────────────────────────────────
   THE WEIGHTS — one block, tunable, each with its reasoning
   ─────────────────────────────────────────────────────────────────────────────
   `max` is the largest fraction a signal may add to the CD3 base (0.6 = up to +60%).
   Read alongside the ~1000× base span noted at the top of this file: these are refinements
   inside a consequence band, not overrides of it.
   ───────────────────────────────────────────────────────────────────────────── */

export const PRIORITY_WEIGHTS = {
  /**
   * UNBLOCKING VALUE — how many still-open milestones sit downstream of this one.
   *
   * The largest derived weight, because it is the only signal that changes *system* throughput
   * rather than one item's value: landing a chain head releases everything queued behind it, and
   * the firm's constraint is capacity, not any single deliverable. This is the same logic that
   * makes CD3 beat per-job ROI under shared capacity (METHODOLOGY_REGISTER §3).
   *
   * Counted over the FULL downstream chain, not just direct dependents — a milestone at the head
   * of a five-deep chain is a bottleneck in a way a leaf never is, and `Predecessor` is a single
   * link so the graph is a forest and the descendant count is well defined.
   *
   * ⚠️ This contract was written correctly here and implemented WRONG in api/priority.js, which
   * counted direct successors only until 2026-08-30. Because the firm's chains are nearly linear,
   * that made the term ~inert exactly on the roots it exists to surface. If you change this
   * comment, check the implementation still matches it.
   *
   * Saturates at 10, raised from 4 on 2026-08-30 once the count became real. Measured over the
   * live queue: of the 60 startable milestones, 24 gate 4+ others and 12 gate 10+, with a maximum
   * of 24 — so a cap of 4 handed the identical full boost to a milestone gating four and one
   * gating twenty-four. 10 spreads the term across the range that actually occurs while still
   * refusing to let one freakishly long chain buy the top of the whole firm's queue.
   */
  unblocking: { max: 0.60, saturateAt: 10 },

  /**
   * CHRONIC SLIPPAGE — `Schedule Pushes`.
   *
   * Phase 0 Finding 5: the ONE part of today's formula carrying real signal (r = −0.27 against
   * Priority Score), and the top of the live queue is dominated by chronically-pushed work.
   * Repeated pushing is genuine evidence of neglect — nobody chose to deprioritise these, they
   * just kept losing. Carried forward deliberately rather than rediscovered.
   *
   * Keeps the old formula's √ shape on purpose: the 8th push means far less than the 2nd. It is
   * evidence that something is being *avoided*, not evidence that it *matters* — which is why it
   * ranks below unblocking value. A chronically-pushed low-consequence task is still low
   * consequence; this term stops it disappearing, it does not promote it.
   *
   * Saturates at 9 pushes (√9 = 3), which is past the 8+ band where 40 of 219 live milestones sit.
   */
  slippage: { max: 0.40, saturateAt: 9 },

  /**
   * TIME PRESSURE — deliberately the WEAKEST of the three, and deliberately mostly inert.
   *
   * Phase 0's sharpest finding: only 6 of 219 milestones are overdue, yet 40 have been pushed 8+
   * times and one 69 times. **Work here does not go late; the date moves.** A date that gets
   * rewritten whenever it becomes uncomfortable carries almost no discriminating information, so
   * it gets a small, bounded, secondary contribution and never the backbone. Today's score gets
   * this exactly backwards (r = 0.998 against days-left) and that is the thing being replaced.
   *
   * Expect this term to be flat for roughly 75% of the queue and near-full for a handful. **That
   * is the intended reading of Finding 4, not a bug** — if this term starts firing broadly, either
   * the data changed or someone widened it.
   *
   * Shape: nothing beyond `dueSoonDays`; ramps to half strength as the date arrives; ramps to full
   * only once genuinely, uncomfortably overdue (`overdueFullDays`) — i.e. past a date that nobody
   * even bothered to push, which is the one case where the date IS information.
   */
  timePressure: { max: 0.35, dueSoonDays: 14, overdueFullDays: 30 },

  /**
   * OUTSIDE PARTY WAITING — `milestone_behavior` of Reactive or Fixed.
   *
   * A multiplier BELOW 1, not an exclusion. You cannot make an agency move faster by ranking its
   * review higher, so pushing this work up the queue produces no throughput — but it is not zero
   * either, since there is usually a nudge or a follow-up the firm actually owns.
   *
   * Same split docs/PRIORITY_ENGINE.md §8 relies on for the health metric: Active is work the firm
   * controls, Reactive/Fixed is waiting on someone else. Note the scheduler's own population
   * excludes Fixed and Scheduled, so in the preview this fires almost only on Reactive.
   */
  outsideWaiting: { reactive: 0.85 },

  /**
   * PHASE FUEL — the phase's live budget health (api/flights.js#computePhaseFuel).
   *
   * ⚠️ THE ONE WEIGHT WHOSE DIRECTION IS A JUDGEMENT CALL, AND IT NEEDS CONFIRMING.
   * docs/FINANCIAL_TRACKING_PRINCIPLES.md carries this exact lesson — a directional metric with no
   * explicit "which way is better" is a trap. Both readings are defensible:
   *   (a) a phase burning its budget should be LANDED FAST to stop the bleed  → weight it UP
   *   (b) an over-budget phase loses money per hour                           → weight it DOWN
   *
   * (a) is chosen because the old system implicitly did (b) — fee remaining → days budgeted →
   * slack → a burned phase drifts DOWN the queue — and docs/PRIORITY_ENGINE.md §2 names that as a
   * consequence "nobody chose": "exactly the phases most likely to be in trouble" get
   * deprioritised. Reversing it is the point.
   *
   * Kept small (≤ +15%) precisely because the direction is contested. First candidate for tuning.
   */
  phaseFuel: { 'Over Budget': 1.15, 'At Risk': 1.10, 'Watch': 1.05, 'On Track': 1.00, 'No Budget Set': 1.00 },

  /**
   * BLOCKED — this milestone's own Predecessor has not posted yet, so nobody can start it today.
   *
   * Added 2026-08-29 after the first full-catalogue scoring pass produced a top 20 that was 18/20
   * submission-type milestones — 13 of them Plan Check Resubmissions. The scores were not wrong
   * (a submission genuinely is a tiny act with an enormous downstream consequence, 8 ÷ 1), but the
   * ranking was UNACTIONABLE: a resubmission cannot happen until the corrections it depends on are
   * drafted, and that is a DIFFERENT record scored 8 ÷ 5. The queue was recommending work nobody
   * could pick up.
   *
   * A MULTIPLIER, NOT AN EXCLUSION — deliberately unlike the Flight Plan, which drops blocked
   * milestones from the flight line entirely (api/flights.js#isBlocked, "no point in showing
   * flights in the flight line that are blocked by other flights"). That is right for a board of
   * what you can fly right now; it is wrong for a ranking whose job is to say what matters. A
   * blocked, high-consequence milestone is real information — it says GO UNBLOCK THIS — and hiding
   * it would lose exactly the signal that makes its predecessor worth doing.
   *
   * 0.15 sinks a blocked 8÷1 submission (8.0 → 1.2) below an unblocked 8÷5 revision (1.6), so the
   * work that clears the path outranks the work waiting on it, while still leaving it above
   * unblocked low-consequence work (BIM coordination at 0.6). That ordering is the point of the
   * weight, and the number to tune if the balance feels wrong.
   *
   * FAILS OPEN: unknown blocked-ness returns 1 (treated as ready), matching isBlocked's own
   * behaviour when Airtable cannot be reached. Suppressing real work on data we cannot trust is
   * worse than briefly over-ranking something.
   */
  blocked: { multiplier: 0.15 },

  /**
   * REMAINING EFFORT — CD3's denominator is time-until-the-benefit-lands, not total job size.
   *
   * Added 2026-08-29. The first implementation divided by the TYPE's total effort, which is wrong
   * for anything already part-done and badly wrong for anything finished. A live audit found **30
   * of 219 milestones with a 100%-complete checklist that were never posted** — genuinely done
   * work, invisible because nothing in the system ever asked anyone to close the loop. Those
   * milestones rank by the effort of doing the whole job when the remaining job is "open the post
   * window."
   *
   * The distortion was arbitrary rather than merely conservative: Agan's resubmission surfaced at
   * #1 only because its TYPE is scored effort 1, while Luisi's finished Conceptual Framework sank
   * because its type is scored 5. Same readiness, opposite rank, decided by type rather than state.
   *
   * Matt's call, and the right one — no special "ready to land" band, no curated list to maintain:
   * fix the denominator and finished work surfaces on its own, by the ordinary rule.
   *
   *   remaining = max(effort × (1 − percentComplete), floor)
   *
   * FLOOR exists because a 100%-complete milestone has ~0 remaining duration and CD3 would go to
   * infinity. 0.5 (half a Fibonacci step) makes a finished effort-5 milestone score 10× its
   * untouched self — decisive without being unbounded. **The number to tune if finished work
   * dominates the queue too hard.**
   *
   * Reads `Percent Complete Checklist Items`. In this firm that field is effectively BINARY today
   * (the same audit found 30 rows at 100%, 189 under 25%, and **zero in between**), so in practice
   * this currently behaves as a large boost for finished work. It grades smoothly on its own if
   * checklists ever start being ticked mid-flight — no change needed then.
   */
  remainingEffort: { floor: 0.5 },
}

/* ─────────────────────────────────────────────────────────────────────────────
   Resolution — milestone override → deliverable type → nothing
   ───────────────────────────────────────────────────────────────────────────── */

/** Index `scores` (from /api/priority `list`) for lookup: `idx[scopeKind][scopeKey]`. */
export function indexScores(scores) {
  const idx = { deliverable_type: {}, thread_type: {}, milestone: {}, thread: {} }
  for (const s of (scores || [])) {
    if (idx[s.scopeKind]) idx[s.scopeKind][s.scopeKey] = s
  }
  return idx
}

/**
 * Shared by both resolvers. Each HALF resolves independently — a milestone override that sets only
 * effort must still inherit cost-of-delay from its type, and the UI has to be able to say which
 * half came from where. (Phase 1a's version reported one `source` for both halves, which would
 * have shown an inherited cost-of-delay as if someone had scored this specific milestone. §7's
 * whole point is that a number which looks deliberate but isn't is worse than no number.)
 */
function resolvePair(own, type, ownKind) {
  const pick = (field) => {
    if (own && own[field] != null) return { value: own[field], source: ownKind }
    if (type && type[field] != null) return { value: type[field], source: 'type' }
    return { value: null, source: null }
  }
  const cod = pick('costOfDelay')
  const eff = pick('effort')
  return {
    costOfDelay: cod.value, effort: eff.value,
    codSource: cod.source, effortSource: eff.source,
    // Retained for callers that only want a single word for the row. Reports the strongest source
    // present, so a half-override still reads as an override overall.
    source: (cod.source === ownKind || eff.source === ownKind) ? ownKind
          : (cod.source === 'type' || eff.source === 'type') ? 'type' : null,
    // The reviewed_at that actually applied, so staleness is reported against the row that was
    // used rather than against whichever row happens to exist.
    reviewedAt: (cod.source === ownKind || eff.source === ownKind)
      ? (own && own.reviewedAt) || null
      : (type && type.reviewedAt) || null,
  }
}

/**
 * A milestone's effective score: its own override first, then its deliverable type, then nothing.
 * Returns `{ costOfDelay, effort, codSource, effortSource, source, reviewedAt }` where a source is
 * 'milestone' | 'type' | null.
 */
export function resolveMilestoneScore(idx, milestoneId, deliverableTypeId) {
  return resolvePair(
    milestoneId ? idx.milestone[milestoneId] : null,
    deliverableTypeId ? idx.deliverable_type[deliverableTypeId] : null,
    'milestone',
  )
}

/** Same resolution for threads: the thread's own score, else its Type's. */
export function resolveThreadScore(idx, threadId, threadType) {
  return resolvePair(
    threadId ? idx.thread[threadId] : null,
    threadType ? idx.thread_type[threadType] : null,
    'thread',
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
   The multipliers
   ───────────────────────────────────────────────────────────────────────────── */

const clamp01 = (n) => n < 0 ? 0 : n > 1 ? 1 : n

/** Linear to a cap. n open milestones downstream → 1 … 1 + max. */
export function unblockingMultiplier(openDownstream, w = PRIORITY_WEIGHTS.unblocking) {
  const n = Number(openDownstream || 0)
  if (!(n > 0)) return 1
  return 1 + w.max * clamp01(n / w.saturateAt)
}

/** √pushes to a cap — the 8th push moves this far less than the 2nd, same as the old formula. */
export function slippageMultiplier(pushes, w = PRIORITY_WEIGHTS.slippage) {
  const p = Number(pushes || 0)
  if (!(p > 0)) return 1
  return 1 + w.max * clamp01(Math.sqrt(p) / Math.sqrt(w.saturateAt))
}

/**
 * Piecewise: flat until `dueSoonDays` out, half strength at the due date itself, full strength only
 * once `overdueFullDays` past it. `daysLeft` null (no date) contributes nothing rather than
 * guessing — an undated milestone is not urgent, it is unmeasured.
 */
export function timePressureMultiplier(daysLeft, w = PRIORITY_WEIGHTS.timePressure) {
  if (daysLeft == null || !Number.isFinite(Number(daysLeft))) return 1
  const d = Number(daysLeft)
  if (d > w.dueSoonDays) return 1
  const f = d >= 0
    ? 0.5 * (1 - d / w.dueSoonDays)                       // 14d out → 0 … due today → 0.5
    : 0.5 + 0.5 * clamp01(-d / w.overdueFullDays)         // due today → 0.5 … 30d over → 1.0
  return 1 + w.max * clamp01(f)
}

/** Reactive/Fixed = someone outside the firm is holding this. Down-weighted, never excluded. */
export function outsideWaitingMultiplier(behavior, w = PRIORITY_WEIGHTS.outsideWaiting) {
  return (behavior === 'Reactive' || behavior === 'Fixed') ? w.reactive : 1
}

/** Unknown or unavailable fuel returns 1 — fail flat, never guess a budget. */
export function phaseFuelMultiplier(fuelStatus, w = PRIORITY_WEIGHTS.phaseFuel) {
  if (!fuelStatus) return 1
  return w[fuelStatus] == null ? 1 : w[fuelStatus]
}

/** Waiting on its own predecessor. Only an explicit `true` penalises — unknown fails open. */
export function blockedMultiplier(blocked, w = PRIORITY_WEIGHTS.blocked) {
  return blocked === true ? w.multiplier : 1
}

/* ─────────────────────────────────────────────────────────────────────────────
   The score
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * Compute one row's shadow priority.
 *
 * `tier` is the honesty field and the caller must surface it:
 *   'scored'   both halves resolved — a real CD3 value
 *   'partial'  exactly one half resolved — CD3 is undefined, so this row CANNOT be ranked
 *              economically and falls back. Distinct from 'unscored' so the curation surface can
 *              chase the missing half rather than treating it as untouched.
 *   'unscored' neither half — falls back to today's Priority Score ordering
 *
 * Only 'scored' rows carry a `score`. There is no default-to-median for a missing half: inventing
 * a cost of delay is exactly the false precision the Fibonacci scale exists to forbid, and
 * docs/PRIORITY_ENGINE.md's degradation rule is explicit — unscored falls back to the old ranking,
 * it does not get made up.
 */
export function computePriority({ costOfDelay, effort, dial, signals } = {}) {
  const cod = costOfDelay == null ? null : Number(costOfDelay)
  const eff = effort == null ? null : Number(effort)
  const d = (dial == null || !Number.isFinite(Number(dial))) ? 1 : Number(dial)
  const s = signals || {}

  const multipliers = {
    unblocking: unblockingMultiplier(s.openDownstream),
    slippage: slippageMultiplier(s.pushes),
    timePressure: timePressureMultiplier(s.daysLeft),
    outsideWaiting: outsideWaitingMultiplier(s.behavior),
    phaseFuel: phaseFuelMultiplier(s.fuelStatus),
    blocked: blockedMultiplier(s.blocked),
  }
  const modifier = multipliers.unblocking * multipliers.slippage * multipliers.timePressure
                 * multipliers.outsideWaiting * multipliers.phaseFuel * multipliers.blocked

  const tier = (cod != null && eff != null) ? 'scored'
             : (cod != null || eff != null) ? 'partial' : 'unscored'

  if (tier !== 'scored') return { tier, base: null, modifier, multipliers, score: null }

  // CD3 divides by REMAINING duration, not total job size — see PRIORITY_WEIGHTS.remainingEffort.
  // Missing/unknown percent complete reads as 0 (nothing done), so an absent signal never inflates
  // a score; it just leaves the denominator at full effort, exactly as before this existed.
  const pct = clamp01(Number(s.percentComplete) || 0)
  const remainingEffort = Math.max(eff * (1 - pct), PRIORITY_WEIGHTS.remainingEffort.floor)

  const base = (cod * d) / remainingEffort
  return { tier, base, modifier, multipliers, score: base * modifier, percentComplete: pct, remainingEffort }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Comparison arithmetic — kept here so the number the UI prints is the number the
   server computed, not a second implementation of the same statistic.
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * Spearman rank correlation between two rankings of the same items.
 * `pairs` is [[rankA, rankB], …] with ranks already 1..n and no ties (both come from a sort, so
 * the simple formula is exact here — no tie correction needed). Returns null below n = 3, where
 * the statistic is meaningless rather than merely noisy.
 */
export function spearman(pairs) {
  const n = (pairs || []).length
  if (n < 3) return null
  let sumD2 = 0
  for (const [a, b] of pairs) sumD2 += (a - b) * (a - b)
  return 1 - (6 * sumD2) / (n * (n * n - 1))
}
